import {Injectable} from '@angular/core';
import {DatePipe} from '@angular/common';
import memo from 'memo-decorator';

import {AppSettingsService} from './app-settings.service';
import MetaFileLink from '../models/metaFileLink.model';
import {LocationDetailsResponse} from '../shared/models/LocationDetailsResponse.model';
import {ResourceService} from './resource.service';
import {DialogService} from './dialog.service';
import {AlertDialogParams} from '../shared/components/alert-dialog/alert-dialog.component';
import HoldingDiscriminator from '../shared/enums/HoldingDiscriminator.enum';
import {BaseResponseDto, BaseResponseStatus} from '../shared/models/BaseResponseDto.model';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {
  constructor(
    private resourceService: ResourceService,
    private datePipe: DatePipe,
    private appSettingsService: AppSettingsService,
    private dialogService: DialogService) {
  }

  getReportPeriodText(periodStart: Date, periodEnd: Date) {
    periodStart = new Date(periodStart);
    periodEnd = new Date(periodEnd);

    if (this.isSameYear(periodStart, periodEnd)) {
      const periodStartMonth = periodStart.getMonth();

      if (periodStartMonth === 0 && periodStart.getDate() === 1 && periodEnd.getMonth() === 11 && periodEnd.getDate() === 31) {
        return `Year ${periodStart.getFullYear()}`;
      }

      // Definitions:
      const qStartMonths = [0, 3, 6, 9];
      const qEndDate = [31, 30, 30, 31];

      if (qStartMonths.findIndex(x => x === periodStartMonth) !== -1 && periodEnd.getMonth() === periodStartMonth + 2) {
        if (periodStart.getDate() === 1) {
          // Check that the end date of the end period is the last datte in the month:
          const quarter = qStartMonths.findIndex(x => x === periodStartMonth) + 1;
          if (periodEnd.getDate() === qEndDate[quarter - 1]) {
            return `Q${quarter} ${periodStart.getFullYear()}`;
          }
        }
      }
    }
    return `${this.datePipe.transform(periodStart)} - ${this.datePipe.transform(periodEnd)}`;
  }

  private isSameYear(periodStart: Date, periodEnd: Date) {
    return periodStart.getFullYear() === periodEnd.getFullYear();
  }

  downloadFileFromUrl(fileName: string) {
    const a = document.createElement('a');
    a.href = fileName;
    a.target = '_blank';
    a.download = fileName;
    a.dispatchEvent(new MouseEvent('click'));
  }

  /**
   * Downloads a file from the server.
   * The fileTitle parameter is OPTIONAL, and is only used in case the browser uses
   * the a>download attribute as the suggested name in the save-as dialog
   */
  public downloadFile(metaFileLink: MetaFileLink, fileTitle = '') {
    const link = document.createElement('a');
    link.href = metaFileLink.url;
    link.download = fileTitle;
    link.dispatchEvent(new MouseEvent('click'));
  }

  @memo({resolver: (...args: any[]) => args[0] ? args[0].id : '' + '_' + args[1]})
  public getFormattedAddress(location: LocationDetailsResponse, countryFormat: 'fullName' | 'code' = 'code') {
    if (!location) {
      return '';
    }

    const country = this.resourceService.getCountryById(location.countryId);
    let countryString = null;
    if (country) {
      if (countryFormat === 'code') {
        countryString = country.code;
      } else {
        countryString = country.name;
      }
    }

    const orderedAddressParts = [
      location.houseNumber,
      location.streetName,
      location.cityName,
      location.stateName,
      location.postalCode,
      countryString
    ];

    const formattedAddress = orderedAddressParts.filter(el => !!el).join(', ');
    return formattedAddress;
  }

  holdingDownInfo(discriminator: HoldingDiscriminator = HoldingDiscriminator.Asset) {
    const discStrCapitalized = HoldingDiscriminator.toString(discriminator);
    const discStrLowerCase = HoldingDiscriminator.toString(discriminator).toLowerCase();
    this.dialogService.alertDialog(new AlertDialogParams(`${discStrCapitalized} is down`,
      `<p>When the ${discStrLowerCase} is down, investors will not receive system updates, and the ${discStrLowerCase} will not be available on their Investor Portal. We recommend moving the
      ${discStrLowerCase} to down mode when you want to add information to the ${discStrLowerCase} retroactively, without notifying your investors about each of the updates that you’re
      making. Once you’re done making changes, put the ${discStrLowerCase} back up to make it available again on the Investor Portal.</p>
      <h3>How do I change it?</h3>
      <p>The Down/Up setting can be changed from the ${discStrLowerCase} page, by clicking the Edit ${discStrCapitalized} button (top right corner), under "More settings" tab.</p>`));
  }

  /**
   * Indicates if the user on mobile as we check in SCSS breakpoints (width < 650px)
   */
  isMobileScreenSize(): boolean {
    return window.innerWidth < 650;
  }

  alertErrorMessage(error: BaseResponseDto) {
    const dialogParams = new AlertDialogParams();
    dialogParams.title = this.getErrorMessageTitle(error.responseStatus);
    dialogParams.description = error?.error?.exceptionMessage || error?.responseMessage || 'An error has occurred, please contact support.';
    this.dialogService.alertDialog(dialogParams);
  }

  private getErrorMessageTitle(responseStatus: BaseResponseStatus): string {
    let title = '';
    switch (responseStatus) {
      case BaseResponseStatus.Notice:
        title = 'Notice';
        break;
      case BaseResponseStatus.Error:
        title = 'Error';
        break;
      case BaseResponseStatus.NoAccessToResource:
      case BaseResponseStatus.NoWritePermission:
        title = 'You do not have permissions to perform this action';
        break;
      case BaseResponseStatus.NotAuthorizedSignatory:
        title = 'You do not have financial-signatory permissions';
        break;
      default:
        title = 'Attention';
        break;
    }

    return title;
  }

  alertMaxRetries() {
    const dialogParams = new AlertDialogParams('Attention', 'You have exceeded the maximum number of resend attempts allowed');
    this.dialogService.alertDialog(dialogParams)
      .afterClosed()
      .subscribe(_ => {
      });
  }
}
